import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "components/Header";
import CommonModal from "components/modals/CommonModal";
import { handleFileUpload } from "utils/common";
import { useDispatch, useSelector } from "react-redux";
import { setConfirmModalConfig } from "store/slices/utilSlice";
import { useBulkCreateProductsMutation, useGetColorsQuery } from "services";
import { tokens } from "theme";
import dataGridStyles from "../../styles/dataGridStyles";

const BulkImport = () => {
  const theme = useTheme();
  const styles = dataGridStyles(theme.palette.mode);
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [bulkCreateProducts, { isLoading: isImporting }] =
    useBulkCreateProductsMutation();

  // State
  const [step, setStep] = useState(1); // 1: upload images, 2: download template, 3: upload filled CSV
  const [colorsData, setColorsData] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, stage: "" });
  const [results, setResults] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [colorInputs, setColorInputs] = useState([{ name: "", hexCode: "", files: [] }]);

  // Master colors dropdown — populate from admin master data
  const { data: masterColors = [] } = useGetColorsQuery();
  const masterColorsList = masterColors?.items || masterColors || [];

  // Snackbar helper
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Step 1: Color image uploads
  const handleColorFileChange = (colorIndex, event, type) => {
    const files = Array.from(event.target.files);
    const newColorInputs = [...colorInputs];
    newColorInputs[colorIndex] = {
      ...newColorInputs[colorIndex],
      files: [...newColorInputs[colorIndex].files, ...files],
    };
    setColorInputs(newColorInputs);
  };

  const removeColorFile = (colorIndex, fileIndex) => {
    const newColorInputs = [...colorInputs];
    newColorInputs[colorIndex].files = newColorInputs[colorIndex].files.filter((_, i) => i !== fileIndex);
    setColorInputs(newColorInputs);
  };

  const addColorRow = () => {
    setColorInputs([...colorInputs, { name: "", hexCode: "", files: [] }]);
  };

  const removeColorRow = (index) => {
    if (colorInputs.length > 1) {
      setColorInputs(colorInputs.filter((_, i) => i !== index));
    }
  };

  // Upload images to Firebase Storage (same as single product add)
  const uploadImagesToFirebase = async () => {
    if (colorInputs.some((c) => !c.name || !c.hexCode || c.files.length === 0)) {
      showSnackbar("Please fill color name, hex code, and add at least one image for each color", "error");
      return;
    }

    setProcessing(true);
    const totalFiles = colorInputs.reduce((sum, c) => sum + c.files.length, 0);
    setProgress({ current: 0, total: totalFiles, stage: "Uploading images to Firebase Storage..." });

    try {
      const uploadedUrls = {};
      let uploaded = 0;

      // Upload each color's images to Firebase Storage
      for (const color of colorInputs) {
        const colorKey = color.name;
        if (!uploadedUrls[colorKey]) uploadedUrls[colorKey] = { main: null, extra: [] };

        for (let idx = 0; idx < color.files.length; idx++) {
          const file = color.files[idx];
          const isMain = idx === 0;

          // Use the existing handleFileUpload utility (Firebase Storage)
          await new Promise((resolve, reject) => {
            handleFileUpload(
              file,
              `products/${color.hexCode.replace("#", "")}-${color.name}`,
              (progress) => {
                // Update progress for this file
              },
              (downloadURL) => {
                if (isMain) {
                  uploadedUrls[colorKey].main = downloadURL;
                } else {
                  uploadedUrls[colorKey].extra.push(downloadURL);
                }
                uploaded++;
                setProgress((prev) => ({ ...prev, current: uploaded }));
                resolve();
              }
            );
          });
        }
      }

      // Build colorsData for CSV template
      const builtColorsData = colorInputs.map((color) => {
        const urls = uploadedUrls[color.name] || { main: "", extra: [] };
        return {
          name: color.name,
          hexCode: color.hexCode,
          mainImage: urls.main || "",
          images: urls.extra || [],
        };
      });

      setColorsData(builtColorsData);
      setStep(2);
      showSnackbar("Images uploaded successfully! Now download the CSV template.");
    } catch (error) {
      console.error("Image upload error:", error);
      showSnackbar(error?.message || "Failed to upload images", "error");
    } finally {
      setProcessing(false);
    }
  };

  // Step 2: Generate and download XLSX template (Excel-compatible)
  // Pre-built .xlsx file with formatting: orange main image highlight, purple group, big headers
  const generateCsvTemplate = () => {
    if (colorsData.length === 0) {
      showSnackbar("Please upload images first", "error");
      return;
    }

    // Fetch the pre-built .xlsx template from public/ folder
    const link = document.createElement("a");
    link.href = "/bulk-import-template.xlsx";
    link.download = "bulk-import-template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showSnackbar("✅ .xlsx template downloaded! Fill it and upload in next step.");
    setStep(3);
  };

  // Step 3: Parse uploaded CSV
  const handleCsvFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.trim().split("\n");
      if (lines.length < 2) {
        showSnackbar("CSV must have at least header and one data row", "error");
        return;
      }

      const headers = lines[0].split(",").map((h) => h.replace(/"/g, "").trim());
      const rows = lines.slice(1).map((line) => {
        const values = parseCsvLine(line);
        const row = {};
        headers.forEach((header, i) => {
          row[header] = values[i] || "";
        });
        return row;
      });

      setCsvData(rows);
      showSnackbar(`${rows.length} products parsed from CSV. Ready to import!`);
    };
    reader.readAsText(file);
  };

  // Simple CSV parser handling quoted fields
  const parseCsvLine = (line) => {
    const result = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        result.push(current);
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current);
    return result.map((v) => v.replace(/^"|"$/g, ""));
  };

  // Convert CSV row to product payload
  const convertRowToProduct = (row) => {
    const colorImages = row.colorImages ? JSON.parse(row.colorImages) : [];
    const colors = row.colors ? row.colors.split(";").map((c) => {
      const [name, hexCode] = c.split(":");
      return { name, hexCode };
    }) : [];

    const sizes = row.sizes ? row.sizes.split(";").map((s) => {
      const [size, unitPrice, sellingPrice, minqty] = s.split(":");
      return { size, unitPrice: Number(unitPrice), sellingPrice: Number(sellingPrice), minqty: Number(minqty), productType: "B2C" };
    }) : [];

    const sections = row.sections ? row.sections.split(";") : [];
    const occasion = row.occasion ? row.occasion.split(";") : [];
    const fabric = row.fabric ? row.fabric.split(";") : [];
    const tags = row.tags ? row.tags.split(";") : [];
    const sizeType = row.sizeType ? row.sizeType.split(";") : [];
    const keywords = row.keywords ? row.keywords.split(",").map((k) => k.trim()) : [];

    return {
      sku: row.sku,
      name: row.name,
      categoryId: row.categoryId,
      brand: row.brand,
      description: row.description,
      additionalInformation: row.additionalInformation,
      originalPrice: Number(row.originalPrice),
      sellingPrice: Number(row.sellingPrice),
      discount: Number(row.discount),
      discountLabel: row.discountLabel,
      sections,
      occasion,
      fabric,
      length: row.length,
      pattern: row.pattern,
      tags,
      isSize: row.isSize === "true",
      sizeType,
      sizes,
      colors: colorImages.map((c) => ({
        name: c.name,
        hexCode: c.hexCode,
        images: {
          mainImage: { url: c.mainImage, mediaType: "image" },
          images: c.images?.map((img) => ({ url: img, mediaType: "image" })) || [],
        },
      })),
      b2cPrice: {
        originalPrice: Number(row.originalPrice),
        sellingPrice: Number(row.sellingPrice),
        discount: Number(row.discount),
      },
      seo: {
        metaTitle: row.metaTitle,
        metaDescription: row.metaDescription,
        keywords,
      },
      shopId: row.shopId || import.meta.env.VITE_SHOP_ID,
      stock: sizes,
    };
  };

  // Upload CSV and create products
  const importProducts = async () => {
    if (csvData.length === 0) {
      showSnackbar("Please upload a CSV file first", "error");
      return;
    }

    const confirmed = window.confirm(`Import ${csvData.length} products? This cannot be undone.`);
    if (!confirmed) return;

    setProcessing(true);
    setProgress({ current: 0, total: csvData.length, stage: "Creating products..." });

    try {
      const products = csvData.map(convertRowToProduct);

      const res = await bulkCreateProducts({ products }).unwrap();
      const data = res.data || res;

      if (!data.created) throw new Error("Import failed");

      setResults(data);
      showSnackbar(`Import completed: ${data.created.length} created, ${data.failed.length} failed`);
      setStep(4);
    } catch (error) {
      console.error("Import error:", error);
      showSnackbar(error?.data?.message || error?.message || "Import failed", "error");
    } finally {
      setProcessing(false);
    }
  };

  // Download error report
  const downloadErrorReport = () => {
    if (!results?.failed?.length) return;

    const headers = ["sku", "name", "error"];
    const rows = results.failed.map((f) => [
      f.row?.sku || "",
      f.row?.name || "",
      f.error || "",
    ]);

    const csvContent = [headers.join(","), ...rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "import-errors.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // Reset form
  const resetForm = () => {
    setStep(1);
    setColorsData([]);
    setCsvData([]);
    setResults(null);
    setColorInputs([{ name: "", hexCode: "", files: [] }]);
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Header title="BULK IMPORT PRODUCTS" subtitle="Upload images, generate CSV template, and import products in bulk" />

      {/* Progress Steps Indicator */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} sx={{ justifyContent: "center" }}>
          {[1, 2, 3, 4].map((s) => (
            <Grid item key={s} xs={3} sx={{ textAlign: "center" }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 8px",
                  backgroundColor: step >= s ? colors.blueAccent[600] : "grey.300",
                  color: step >= s ? "white" : "grey.600",
                  fontWeight: 600,
                  transition: "all 0.3s",
                }}
              >
                {step > s ? <VisibilityIcon fontSize="small" /> : s}
              </Box>
              <Typography variant="caption" color={step >= s ? "primary.main" : "text.secondary"}>
                {["Upload Images", "Download Template", "Upload CSV", "Results"][s - 1]}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Step 1: Upload Color Images */}
      {step === 1 && (
        <Card sx={{ mb: 3 }}>
          <CardHeader title="Step 1: Upload Color Images" subheader="Add colors with their images. Images will be uploaded to Firebase Storage automatically and URLs will be pre-filled in CSV template." />
          <CardContent>
            <Grid container spacing={3}>
              {colorInputs.map((color, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          options={masterColorsList}
                          getOptionLabel={(option) => option?.name || ""}
                          value={masterColorsList?.find((c) => c.name === color.name) || null}
                          onChange={(_, value) => {
                            const newInputs = [...colorInputs];
                            newInputs[index] = {
                              ...newInputs[index],
                              name: value?.name || "",
                              hexCode: value?.hexCode || "",
                            };
                            setColorInputs(newInputs);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              label="Select Color (Master)"
                              variant="outlined"
                              placeholder="Choose master color"
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Hex (Auto from Master)"
                          value={color.hexCode || ""}
                          disabled
                          variant="outlined"
                          InputProps={{
                            startAdornment: <Box sx={{ width: 24, height: 24, borderRadius: "50%", bgcolor: color.hexCode, border: "1px solid", borderColor: "divider" }} />,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        {colorInputs.length > 1 && (
                          <IconButton onClick={() => removeColorRow(index)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Grid>
                    </Grid>

                    <Typography variant="caption" sx={{ display: "block", mt: 1, mb: 1, color: "text.secondary" }}>
                      Images ({color.files.length})
                    </Typography>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => handleColorFileChange(index, e)}
                      style={{ display: "none" }}
                      id={`color-files-${index}`}
                    />
                    <Button
                      variant="outlined"
                      component="label"
                      htmlFor={`color-files-${index}`}
                      startIcon={<CloudUploadIcon fontSize="small" />}
                      fullWidth
                      size="small"
                    >
                      Select Images
                    </Button>

                    {color.files.length > 0 && (
                      <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {color.files.map((file, fi) => (
                          <Box key={fi} sx={{ position: "relative", width: 60, height: 60 }}>
                            <img
                              src={URL.createObjectURL(file)}
                              alt={file.name}
                              style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 1 }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => removeColorFile(index, fi)}
                              sx={{ position: "absolute", top: 2, right: 2 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ mt: 2, display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button variant="outlined" onClick={addColorRow} startIcon={<AddIcon />}>
                Add Another Color
              </Button>
              <Button
                variant="contained"
                onClick={uploadImagesToFirebase}
                disabled={processing || colorInputs.length === 0}
                startIcon={<CloudUploadIcon />}
              >
                {processing ? <CircularProgress size={20} color="inherit" /> : "Upload Images to Firebase"}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Download CSV Template */}
      {step === 2 && (
        <Card sx={{ mb: 3 }}>
          <CardHeader title="Step 2: Download CSV Template" subheader="Your uploaded images have been processed. Download the template, fill in product data, then upload in next step." />
          <CardContent>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
              <Typography variant="body1" sx={{ flex: 1, minWidth: 300 }}>
                <strong>{colorsData.length} colors</strong> with images uploaded and ready.
                The template includes pre-filled image URLs for each color.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={generateCsvTemplate}
                startIcon={<FileDownloadIcon />}
              >
                Download CSV Template
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Upload Filled CSV */}
      {step === 3 && (
        <Card sx={{ mb: 3 }}>
          <CardHeader title="Step 3: Upload Filled CSV" subheader="Fill the downloaded CSV template with your product data, then upload it here." />
          <CardContent>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
              <input
                type="file"
                accept=".csv"
                onChange={handleCsvFileChange}
                id="csv-upload"
                style={{ display: "none" }}
              />
              <Button
                variant="outlined"
                component="label"
                htmlFor="csv-upload"
                startIcon={<CloudUploadIcon />}
              >
                Select CSV File
              </Button>
              {csvData.length > 0 && (
                <>
                  <Typography variant="body1" color="success.main">
                    ✓ {csvData.length} products parsed and ready to import
                  </Typography>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={importProducts}
                    disabled={processing}
                    startIcon={<SaveIcon />}
                    size="large"
                  >
                    {processing ? <CircularProgress size={20} color="inherit" /> : "Import Products"}
                  </Button>
                </>
              )}
            </Box>

            {/* Progress Bar */}
            {processing && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  {progress.stage} ({progress.current}/{progress.total})
                </Typography>
                <CircularProgress
                  variant="determinate"
                  value={(progress.current / progress.total) * 100}
                  sx={{ width: "100%", height: 8 }}
                />
              </Box>
            )}

            {/* Preview Table */}
            {csvData.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Preview (first 5 rows):
                </Typography>
                <TableContainer sx={{ maxHeight: 300, overflow: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>SKU</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Price</TableCell>
                        <TableCell>Colors</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {csvData.slice(0, 5).map((row, i) => (
                        <TableRow key={i}>
                          <TableCell>{row.sku}</TableCell>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>{row.categoryId}</TableCell>
                          <TableCell>₹{row.sellingPrice}</TableCell>
                          <TableCell>{row.colors}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 4: Results */}
      {step === 4 && results && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="Import Results"
            subheader={`${results.created.length} created, ${results.failed.length} failed out of ${results.total}`}
          />
          <CardContent>
            {results.created.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" color="success.main" gutterBottom>
                  Successfully Created ({results.created.length})
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>SKU</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>ID</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.created.map((p, i) => (
                        <TableRow key={i}>
                          <TableCell>{p.sku}</TableCell>
                          <TableCell>{p.name}</TableCell>
                          <TableCell>{p._id}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {results.failed.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" color="error.main" gutterBottom>
                  Failed ({results.failed.length})
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>SKU</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.failed.map((f, i) => (
                        <TableRow key={i}>
                          <TableCell>{f.row?.sku}</TableCell>
                          <TableCell>{f.row?.name}</TableCell>
                          <TableCell color="error">{f.error}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Button variant="contained" onClick={resetForm} startIcon={<AddIcon />}>
                Import More Products
              </Button>
              <Button variant="outlined" onClick={() => navigate("/products")} startIcon={<VisibilityIcon />}>
                View Products List
              </Button>
              {results.failed.length > 0 && (
                <Button variant="outlined" color="error" onClick={downloadErrorReport} startIcon={<FileDownloadIcon />}>
                  Download Error Report
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <div style={{ backgroundColor: snackbar.severity === "error" ? "#d32f2f" : "#388e3c", color: "white", padding: "12px 24px", borderRadius: 4 }}>
          {snackbar.message}
        </div>
      </Snackbar>
    </Box>
  );
};

export default BulkImport;